import React, { useContext, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory, useParams } from "react-router-dom";
import { Avatar, Dialog, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentPage from "components/contentPage/ContentPage";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { BranchFavouriteContext } from "contexts/BranchFavouriteContext";
import { MaterialContext } from "contexts/MaterialsContext";
import {
  useQueryCateringEvent,
  useQueryCateringEventMenuDetails,
} from "hooks/CateringEvents";
import {
  CateringEventStatus,
  ICateringEventView,
  ICateringEventDetailedView,
  IMenuItemBreakdownView,
  IMaterialQuantityView,
  CateringEventTier,
  TIER_DISPLAY_NAMES,
  MANPOWER_CATEGORIES,
} from "types/CateringEvent";
import { IMaterial } from "types/Material";
import { MaterialClassification } from "types/MaterialClassification";
import { displayQuantity } from "types/MeasurementUnit";
import { format } from "date-fns";
import styles from "./ViewCateringEvent.module.css";

const formatTime12h = (time24: string): string => {
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${h12}:${mStr} ${ampm}`;
};

const statusStyleMap: Record<CateringEventStatus, string> = {
  [CateringEventStatus.DRAFT]: styles.statusDraft,
  [CateringEventStatus.CREATED]: styles.statusConfirmed,
  [CateringEventStatus.PO_CONSOLIDATED]: styles.statusPoRaised,
  [CateringEventStatus.DONE]: styles.statusCompleted,
};

const ViewCateringEvent: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const history = useHistory();
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  const { branches } = useContext(BranchFavouriteContext);
  const { branchWiseMaterialsMap } = useContext(MaterialContext);
  const { status, eventData, isFetching } = useQueryCateringEvent(eventId);
  const { menuDetails } = useQueryCateringEventMenuDetails(eventId);

  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printRaw, setPrintRaw] = useState(true);
  const [printSemiPrepared, setPrintSemiPrepared] = useState(true);
  const [printAreaVisible, setPrintAreaVisible] = useState(false);

  // Build a flat materialId -> IMaterial lookup from all branches
  const allMaterialsMap = new Map<string, IMaterial>();
  for (const ctx of branchWiseMaterialsMap.values()) {
    for (const [id, mat] of ctx.allMaterialsMap) {
      if (!allMaterialsMap.has(id)) allMaterialsMap.set(id, mat);
    }
  }

  if (status === "loading" || isFetching) {
    return (
      <ContentPage
        header={<div className={styles.pageName}>Loading...</div>}
        mainContent={<div className={styles.emptyState}>Loading event...</div>}
      />
    );
  }

  if (!eventData?.cateringEvent) {
    return (
      <ContentPage
        header={<div className={styles.pageName}>Event Not Found</div>}
        mainContent={
          <div className={styles.emptyState}>
            Event not found.{" "}
            <span className={styles.backLink} onClick={() => history.push("/catering")}>
              Back to events
            </span>
          </div>
        }
      />
    );
  }

  const event = eventData.cateringEvent;
  const branchName = branches.find((b) => b.id === event.branchId)?.name ?? event.branchId;

  const handlePrint = () => {
    setShowPrintDialog(false);
    setPrintAreaVisible(true);
    // Let the portal render, then trigger print
    requestAnimationFrame(() => {
      window.print();
      setPrintAreaVisible(false);
    });
  };

  return (
    <>
      <ContentPage
        header={
          <>
            <div className={styles.pageName}>Catering Event</div>
            <span className={styles.backLink} onClick={() => history.push("/catering")}>
              &larr; Back to Events
            </span>
          </>
        }
        mainContent={
          <div className={styles.container}>
            <EventInfoSection event={event} branchName={branchName} />
            <ManpowerSection event={event} />
            <MenuSection menuDetails={menuDetails ?? null} allMaterialsMap={allMaterialsMap} />
          </div>
        }
        rightSection={
          <RightSection
            event={event}
            menuDetails={menuDetails ?? null}
            primaryColor={primaryColor}
            primaryTextColor={primaryTextColor}
            onEdit={() => history.push(`/catering/events/${event.id}/edit`)}
            onPrint={() => setShowPrintDialog(true)}
          />
        }
      />

      {/* Print Options Dialog */}
      <Dialog open={showPrintDialog} onClose={() => setShowPrintDialog(false)}>
        <div style={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton onClick={() => setShowPrintDialog(false)} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </div>
        <div className={styles.printDialogContent}>
          <div className={styles.printDialogTitle}>Print Options</div>
          <label className={styles.printOption}>
            <input
              type="checkbox"
              checked={printSemiPrepared}
              onChange={(e) => setPrintSemiPrepared(e.target.checked)}
            />
            Include Semi-Prepared Items
          </label>
          <label className={styles.printOption}>
            <input
              type="checkbox"
              checked={printRaw}
              onChange={(e) => setPrintRaw(e.target.checked)}
            />
            Include Raw Materials
          </label>
          <div className={styles.printDialogButtons}>
            <button
              onClick={() => setShowPrintDialog(false)}
              style={{
                padding: "8px 16px",
                border: "1px solid #ccc",
                borderRadius: 4,
                background: "#fff",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handlePrint}
              style={{
                padding: "8px 16px",
                border: "none",
                borderRadius: 4,
                background: primaryColor,
                color: primaryTextColor,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Print
            </button>
          </div>
        </div>
      </Dialog>

      {/* Print-only content — portalled to body so it sits outside #root */}
      {printAreaVisible &&
        ReactDOM.createPortal(
          <PrintableEvent
            event={event}
            branchName={branchName}
            menuDetails={menuDetails ?? null}
            allMaterialsMap={allMaterialsMap}
            includeRaw={printRaw}
            includeSemiPrepared={printSemiPrepared}
          />,
          document.body
        )}
    </>
  );
};

const EventInfoSection: React.FC<{ event: ICateringEventView; branchName: string }> = ({ event, branchName }) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>Event Information</div>
    <div className={styles.detailGrid}>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Status</span>
        <span className={`${styles.statusChip} ${statusStyleMap[event.status]}`}>
          {event.status.replace(/_/g, " ")}
        </span>
      </div>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Name</span>
        <span className={styles.detailValue}>{event.name}</span>
      </div>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Event Date</span>
        <span className={styles.detailValue}>
          {format(new Date(event.eventDate), "dd MMM yyyy")}
          {event.eventTimePerMeal?.[event.mealType] && `, ${formatTime12h(event.eventTimePerMeal[event.mealType]!)}`}
        </span>
      </div>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Venue</span>
        <span className={styles.detailValue}>{branchName}</span>
      </div>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Meal Type</span>
        <span className={styles.detailValue}>
          {event.mealType.charAt(0) + event.mealType.slice(1).toLowerCase()}
        </span>
      </div>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Pax</span>
        <span className={styles.detailValue}>{event.numberOfPeople}</span>
      </div>
      {event.eventType && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Event Type</span>
          <span className={styles.detailValue}>{event.eventType}</span>
        </div>
      )}
      {event.tier && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Tier</span>
          <span className={styles.detailValue}>{TIER_DISPLAY_NAMES[event.tier]}</span>
        </div>
      )}
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Customer Name</span>
        <span className={styles.detailValue}>{event.customerName}</span>
      </div>
      {event.customerContactNumber && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Contact</span>
          <span className={styles.detailValue}>{event.customerContactNumber}</span>
        </div>
      )}
      {event.customerEmail && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Email</span>
          <span className={styles.detailValue}>{event.customerEmail}</span>
        </div>
      )}
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Created By</span>
        <span className={styles.detailValue}>{event.createdByEmployee.name}</span>
      </div>
      <div className={styles.detailItem}>
        <span className={styles.detailLabel}>Created On</span>
        <span className={styles.detailValue}>
          {format(new Date(event.creationTime), "dd MMM yyyy, hh:mm a")}
        </span>
      </div>
      {event.remark && (
        <div className={styles.detailItem} style={{ gridColumn: "1 / -1" }}>
          <span className={styles.detailLabel}>Remarks</span>
          <span className={styles.detailValue}>{event.remark}</span>
        </div>
      )}
      {event.consolidationPointer && (
        <div className={styles.detailItem}>
          <span className={styles.detailLabel}>Consolidation</span>
          <span className={styles.detailValue}>{event.consolidationPointer}</span>
        </div>
      )}
    </div>
  </div>
);

const ManpowerSection: React.FC<{ event: ICateringEventView }> = ({ event }) => {
  if (!event.tier || event.tier === CateringEventTier.NOT_APPLICABLE) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Manpower</div>
      <div className={styles.categoryGroup}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Category</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {MANPOWER_CATEGORIES.map((cat) => (
              <tr key={cat}>
                <td>{cat}</td>
                <td>{event.manpowerEstimates?.[cat] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Build a map of materialId -> total computed quantity (before overrides) by summing per-item computed values
const buildComputedTotals = (
  breakdowns: IMenuItemBreakdownView[],
  type: "raw" | "semiPrepared"
): Map<string, IMaterialQuantityView> => {
  const totals = new Map<string, { qty: number; view: IMaterialQuantityView }>();
  for (const b of breakdowns) {
    const materials = type === "raw" ? b.rawMaterials : b.semiPreparedMaterials;
    for (const m of materials) {
      const existing = totals.get(m.materialId);
      if (existing) {
        existing.qty += m.quantity;
      } else {
        totals.set(m.materialId, { qty: m.quantity, view: m });
      }
    }
  }
  return new Map([...totals.entries()].map(([id, { qty, view }]) => [id, { ...view, quantity: qty }]));
};

// Check if a material has any per-item override
const hasOverride = (
  breakdowns: IMenuItemBreakdownView[],
  materialId: string,
  type: "raw" | "semiPrepared"
): boolean => {
  for (const b of breakdowns) {
    const overrides = type === "raw" ? b.rawMaterialOverrides : b.semiPreparedMaterialOverrides;
    if (overrides && overrides[materialId]) return true;
  }
  return false;
};

const MenuSection: React.FC<{
  menuDetails: ICateringEventDetailedView | null;
  allMaterialsMap: Map<string, IMaterial>;
}> = ({ menuDetails, allMaterialsMap }) => {
  if (!menuDetails) return null;

  const computedRawTotals = buildComputedTotals(menuDetails.menuItemBreakdowns, "raw");
  const computedSemiPreparedTotals = buildComputedTotals(menuDetails.menuItemBreakdowns, "semiPrepared");

  return (
    <>
      {menuDetails.menuItemBreakdowns.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Menu Items</div>
          <div className={styles.categoryGroup}>
            <div className={styles.menuGrid}>
              {menuDetails.menuItemBreakdowns.map((item: IMenuItemBreakdownView) => (
                <div key={item.sellableItemId} className={styles.menuGridItem}>
                  {item.sellableItemName}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {menuDetails.totalSemiPreparedMaterials.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Estimated Semi-Prepared Items</div>
          <div className={styles.categoryGroup}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {menuDetails.totalSemiPreparedMaterials.map((item) => {
                  const isOverridden = hasOverride(menuDetails.menuItemBreakdowns, item.materialId, "semiPrepared");
                  const computed = computedSemiPreparedTotals.get(item.materialId);
                  return (
                    <tr key={item.materialId}>
                      <td>
                        {item.materialName}
                        {isOverridden && computed && (
                          <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                            (est. {displayQuantity(computed.displayQuantity)})
                          </span>
                        )}
                      </td>
                      <td>
                        {displayQuantity(item.displayQuantity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {menuDetails.totalRawMaterials.length > 0 && (
        <MaterialQuantitySection
          title="Estimated Raw Materials"
          items={menuDetails.totalRawMaterials}
          breakdowns={menuDetails.menuItemBreakdowns}
          computedTotals={computedRawTotals}
          allMaterialsMap={allMaterialsMap}
        />
      )}
    </>
  );
};

const groupByClassification = <T extends { materialId: string }>(
  items: T[],
  allMaterialsMap: Map<string, IMaterial>
): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const mat = allMaterialsMap.get(item.materialId);
    const category = mat?.classification ?? "Other";
    const list = groups.get(category) ?? [];
    list.push(item);
    groups.set(category, list);
  }
  // Sort groups by MaterialClassification order, "Other" last
  const classOrder = Object.values(MaterialClassification);
  return new Map(
    [...groups.entries()].sort((a, b) => {
      const ai = classOrder.indexOf(a[0] as MaterialClassification);
      const bi = classOrder.indexOf(b[0] as MaterialClassification);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
  );
};

const MaterialQuantitySection: React.FC<{
  title: string;
  items: IMaterialQuantityView[];
  breakdowns: IMenuItemBreakdownView[];
  computedTotals: Map<string, IMaterialQuantityView>;
  allMaterialsMap: Map<string, IMaterial>;
}> = ({ title, items, breakdowns, computedTotals, allMaterialsMap }) => {
  const grouped = groupByClassification(items, allMaterialsMap);

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {[...grouped.entries()].map(([category, groupItems]) => (
        <div key={category} className={styles.categoryGroup}>
          <div className={styles.categoryGroupLabel}>{category}</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Material</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {groupItems.map((item) => {
                const isOverridden = hasOverride(breakdowns, item.materialId, "raw");
                const computed = computedTotals.get(item.materialId);
                return (
                  <tr key={item.materialId}>
                    <td>
                      {item.materialName}
                      {isOverridden && computed && (
                        <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                          (est. {displayQuantity(computed.displayQuantity)})
                        </span>
                      )}
                    </td>
                    <td>
                      {displayQuantity(item.displayQuantity)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const RightSection: React.FC<{
  event: ICateringEventView;
  menuDetails: ICateringEventDetailedView | null;
  primaryColor: string;
  primaryTextColor: string;
  onEdit: () => void;
  onPrint: () => void;
}> = ({ event, menuDetails, primaryColor, primaryTextColor, onEdit, onPrint }) => {
  const isEditable =
    event.status === CateringEventStatus.DRAFT ||
    event.status === CateringEventStatus.CREATED;

  return (
    <div>
      {isEditable && (
        <button type="button" className={styles.actionBtn} onClick={onEdit}>
          <Avatar className={styles.actionIcon} style={{ backgroundColor: primaryColor }}>
            <span style={{ fontSize: 14, color: primaryTextColor }}>
              {menuDetails?.menuItemBreakdowns.length ? "✎" : "+"}
            </span>
          </Avatar>
          <div className={styles.actionBtnTitle}>
            {menuDetails?.menuItemBreakdowns.length ? "Edit" : "Add Menu Items"}
          </div>
        </button>
      )}
      {event.status !== CateringEventStatus.DRAFT && <button type="button" className={styles.actionBtn} onClick={onPrint}>
        <Avatar className={styles.actionIcon} style={{ backgroundColor: primaryColor }}>
          <span style={{ fontSize: 14, color: primaryTextColor }}>&#x1F5B6;</span>
        </Avatar>
        <div className={styles.actionBtnTitle}>Print</div>
      </button>}
    </div>
  );
};

/* ── Printable layout (portalled to body, visible only during @media print) ── */

const PrintableEvent: React.FC<{
  event: ICateringEventView;
  branchName: string;
  menuDetails: ICateringEventDetailedView | null;
  allMaterialsMap: Map<string, IMaterial>;
  includeRaw: boolean;
  includeSemiPrepared: boolean;
}> = ({ event, branchName, menuDetails, allMaterialsMap, includeRaw, includeSemiPrepared }) => (
  <div id="catering-print-area" className={styles.printArea} style={{ display: "block" }}>
    {/* Header */}
    <h1 className={styles.printEventName}>{event.name}</h1>

    {/* Event Information */}
    <div className={styles.printSection}>
      <div className={styles.printSectionTitle}>Event Information</div>
      <div className={styles.printDetailGrid}>
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Venue</span>
          <span className={styles.printDetailValue}>{branchName}</span>
        </div>
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Event Date</span>
          <span className={styles.printDetailValue}>
            {format(new Date(event.eventDate), "dd MMM yyyy")}
            {event.eventTimePerMeal?.[event.mealType] && `, ${formatTime12h(event.eventTimePerMeal[event.mealType]!)}`}
          </span>
        </div>
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Pax</span>
          <span className={styles.printDetailValue}>{event.numberOfPeople}</span>
        </div>
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Meal Type</span>
          <span className={styles.printDetailValue}>
            {event.mealType.charAt(0) + event.mealType.slice(1).toLowerCase()}
          </span>
        </div>
        {event.eventType && (
          <div className={styles.printDetailItem}>
            <span className={styles.printDetailLabel}>Event Type</span>
            <span className={styles.printDetailValue}>{event.eventType}</span>
          </div>
        )}
        {event.tier && (
          <div className={styles.printDetailItem}>
            <span className={styles.printDetailLabel}>Tier</span>
            <span className={styles.printDetailValue}>{TIER_DISPLAY_NAMES[event.tier]}</span>
          </div>
        )}
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Customer Name</span>
          <span className={styles.printDetailValue}>{event.customerName}</span>
        </div>
        {event.customerContactNumber && (
          <div className={styles.printDetailItem}>
            <span className={styles.printDetailLabel}>Contact</span>
            <span className={styles.printDetailValue}>{event.customerContactNumber}</span>
          </div>
        )}
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Created By</span>
          <span className={styles.printDetailValue}>{event.createdByEmployee.name}</span>
        </div>
        <div className={styles.printDetailItem}>
          <span className={styles.printDetailLabel}>Created On</span>
          <span className={styles.printDetailValue}>
            {format(new Date(event.creationTime), "dd MMM yyyy, hh:mm a")}
          </span>
        </div>
        {event.remark && (
          <div className={styles.printDetailItem} style={{ gridColumn: "1 / -1" }}>
            <span className={styles.printDetailLabel}>Remarks</span>
            <span className={styles.printDetailValue}>{event.remark}</span>
          </div>
        )}
      </div>
    </div>

    {/* Manpower */}
    {event.tier && event.tier !== CateringEventTier.NOT_APPLICABLE && (
      <div className={styles.printSection}>
        <div className={styles.printSectionTitle}>Manpower</div>
        <table className={styles.printTable}>
          <thead>
            <tr>
              <th>Category</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {MANPOWER_CATEGORIES.map((cat) => (
              <tr key={cat}>
                <td>{cat}</td>
                <td>{event.manpowerEstimates?.[cat] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Menu Items */}
    {menuDetails && menuDetails.menuItemBreakdowns.length > 0 && (
      <div className={styles.printSection}>
        <div className={styles.printSectionTitle}>Menu Items</div>
        <ul className={styles.printMenuList}>
          {menuDetails.menuItemBreakdowns.map((item: IMenuItemBreakdownView) => (
            <li key={item.sellableItemId} className={styles.printMenuItem}>
              {item.sellableItemName}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Semi-Prepared Items (flat, no grouping) */}
    {includeSemiPrepared && menuDetails && menuDetails.totalSemiPreparedMaterials.length > 0 && (
      <div className={styles.printSection}>
        <div className={styles.printSectionTitle}>Estimated Semi-Prepared Items</div>
        <table className={styles.printTable}>
          <thead>
            <tr>
              <th>Material</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {menuDetails.totalSemiPreparedMaterials.map((item) => (
              <tr key={item.materialId}>
                <td>{item.materialName}</td>
                <td>{displayQuantity(item.displayQuantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Raw Materials */}
    {includeRaw && menuDetails && menuDetails.totalRawMaterials.length > 0 && (
      <PrintMaterialSection
        title="Estimated Raw Materials"
        items={menuDetails.totalRawMaterials}
        allMaterialsMap={allMaterialsMap}
      />
    )}
  </div>
);

const PrintMaterialSection: React.FC<{
  title: string;
  items: IMaterialQuantityView[];
  allMaterialsMap: Map<string, IMaterial>;
}> = ({ title, items, allMaterialsMap }) => {
  const grouped = groupByClassification(items, allMaterialsMap);

  return (
    <div className={styles.printSection}>
      <div className={styles.printSectionTitle}>{title}</div>
      {[...grouped.entries()].map(([category, groupItems]) => (
        <div key={category}>
          <div className={styles.printCategoryLabel}>{category}</div>
          <table className={styles.printTable}>
            <thead>
              <tr>
                <th>Material</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {groupItems.map((item) => (
                <tr key={item.materialId}>
                  <td>{item.materialName}</td>
                  <td>{displayQuantity(item.displayQuantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default ViewCateringEvent;
